import React, { useEffect, useMemo, useRef, useState } from 'react';

const sessionMessage = 'Your session may have expired. Sign in in another tab, then retry here.';
const responseData = async (response) => {
  if (response.status === 401 || response.status === 403) throw new Error(sessionMessage);
  const contentType = response.headers?.get('content-type') || '';
  if (contentType && !contentType.includes('json')) throw new Error(sessionMessage);
  try {
    return await response.json();
  } catch (_) {
    throw new Error('The server returned an unexpected response. Your session may have expired; sign in in another tab and retry here.');
  }
};
const errorMessages = (data) => Array.isArray(data?.errors) ? data.errors.filter((error) => typeof error === 'string') : [];
const buttonClass = 'rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50';

const BeefreeAssetsPanel = ({ disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');
  const [uploadNotice, setUploadNotice] = useState(null);
  const [copyNotice, setCopyNotice] = useState('');
  const fileInput = useRef(null);
  const urlInputs = useRef(new Map());
  const mounted = useRef(false);
  const pendingLoad = useRef(null);
  const pendingUpload = useRef(false);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingLoad.current?.abort();
    };
  }, []);

  const visibleAssets = useMemo(() => {
    const query = search.trim().toLowerCase();
    return assets.filter((asset) => asset.name.toLowerCase().includes(query));
  }, [assets, search]);

  const loadAssets = async () => {
    if (disabledRef.current || pendingLoad.current || pendingUpload.current) return;
    const controller = new AbortController();
    pendingLoad.current = controller;
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch('/email_assets.json', {
        credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal
      });
      const data = await responseData(response);
      if (!response.ok) throw new Error(errorMessages(data).join(' · ') || 'Could not load images. Try again.');
      if (!Array.isArray(data?.initialAssets)) throw new Error('The image library returned an unexpected response. Try again.');
      if (!mounted.current) return;
      setAssets(data.initialAssets);
      setLoaded(true);
    } catch (error) {
      if (mounted.current && error.name !== 'AbortError') {
        setLoadError(error instanceof TypeError ? 'Could not load images. Check your connection and try again.' : error.message);
      }
    } finally {
      pendingLoad.current = null;
      if (mounted.current) setLoading(false);
    }
  };

  const toggle = () => {
    if (disabledRef.current) return;
    if (!open && !loaded) loadAssets();
    setOpen(!open);
  };

  const upload = async (files) => {
    if (disabledRef.current || pendingUpload.current || pendingLoad.current || !files.length) return;
    pendingUpload.current = true;
    setUploading(true);
    setUploadNotice(null);
    const form = new FormData();
    files.forEach((file) => form.append('files[]', file));
    const token = document.querySelector('[name="csrf-token"]')?.content;
    try {
      const response = await fetch('/email_assets', {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(token ? { 'X-CSRF-Token': token } : {}) }, body: form
      });
      const data = await responseData(response);
      const errors = errorMessages(data);
      if (!response.ok) throw new Error(errors.join(' · ') || 'Could not upload images. Try again.');
      if (!Array.isArray(data?.assets) || !data.assets.length) throw new Error(errors.join(' · ') || 'No uploaded images were returned. Refresh the library before trying again.');
      if (!mounted.current) return;
      setAssets((previous) => [...data.assets, ...previous.filter((asset) => !data.assets.some((created) => created.id === asset.id))]);
      setSearch('');
      setUploadNotice({ success: `Uploaded ${data.assets.length} image${data.assets.length === 1 ? '' : 's'}. Copy a URL below to use it in your email.`, errors });
    } catch (error) {
      if (mounted.current) {
        setUploadNotice({ errors: [error instanceof TypeError ? 'Could not upload images. Check your connection, then refresh the library before retrying.' : error.message] });
      }
    } finally {
      pendingUpload.current = false;
      if (mounted.current) setUploading(false);
    }
  };

  const copy = async (asset) => {
    if (disabledRef.current) return;
    try {
      await navigator.clipboard.writeText(asset.url);
      if (mounted.current) setCopyNotice(`Copied the URL for ${asset.name}. Select an Image block and paste it into the first Url field under IMAGE PROPERTIES.`);
    } catch (_) {
      if (!mounted.current) return;
      if (!disabledRef.current) {
        const input = urlInputs.current.get(asset.id);
        input?.focus();
        input?.select();
      }
      setCopyNotice(`Clipboard access is unavailable. Copy the selected Image URL for ${asset.name} manually, or try Copy URL again.`);
    }
  };

  return (
    <div className='mb-4'>
      <button type='button' onClick={toggle} disabled={disabled} aria-expanded={open} aria-controls='beefree-our-images' className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${open ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}>
        <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round'>
          <rect x='3' y='3' width='18' height='18' rx='3' /><circle cx='8.5' cy='8.5' r='1.5' /><path d='m21 15-5-5L5 21' />
        </svg>
        {open ? 'Hide images' : 'Show images'}
        <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d={open ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
        </svg>
      </button>
      {open && (
        <section id='beefree-our-images' aria-label='Our images' className='mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4'>
          <h2 className='mb-2 text-sm font-semibold text-slate-900'>Our images</h2>
          <p className='text-sm text-slate-700'>Upload here and copy the URL. Select an Image block, then paste into the first Url field under IMAGE PROPERTIES. The Url under ACTION is the click destination.</p>
          <p className='mt-1 text-xs text-slate-500'>The editor’s Browse/Upload and Apply effects may store images with Beefree.</p>
          {assets.some((asset) => /^http:\/\/(?:localhost|127\.0\.0\.1|\[::1\]|[a-z0-9.-]+\.localhost)(?::\d+)?\//i.test(asset.url)) && (
            <p className='mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800'>Local pilot: in Chrome site settings for localhost:3002, allow Apps on device (Приложения на устройстве). Save your work, reload this page, then paste the image URL again. This permission is needed for local images; recipients need public HTTPS image URLs.</p>
          )}
          <div className='mt-3 flex flex-wrap items-center gap-2'>
            <button type='button' onClick={() => fileInput.current?.click()} disabled={disabled || loading || uploading} className={buttonClass}>
              {uploading ? 'Uploading…' : 'Upload images'}
            </button>
            <input
              ref={fileInput} type='file' multiple accept='image/png,image/jpeg,image/gif,image/webp'
              aria-label='Choose images to upload' className='hidden' disabled={disabled || loading || uploading}
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                event.target.value = '';
                upload(files);
              }}
            />
            <button type='button' onClick={loadAssets} disabled={disabled || loading || uploading} className={buttonClass}>Refresh images</button>
            <span className='text-xs text-slate-500'>PNG, JPEG, GIF or WebP, up to 5 MB each.</span>
          </div>
          <label className='mt-3 block text-sm text-slate-700'>
            Search images
            <input type='search' value={search} onChange={(event) => setSearch(event.target.value)} disabled={disabled} placeholder='Search by name…' className='mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50' />
          </label>
          {loadError && (
            <div role='alert' className='mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800'>
              <p>{loadError}</p>
              <button type='button' onClick={loadAssets} disabled={disabled || loading || uploading} className='mt-2 font-medium underline disabled:opacity-50'>Retry loading images</button>
            </div>
          )}
          {uploadNotice?.success && <p role='status' className='mt-3 text-sm text-green-800'>{uploadNotice.success}</p>}
          {uploadNotice?.errors?.length > 0 && (
            <div role='alert' className='mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800'>
              {uploadNotice.errors.map((error, index) => <p key={index}>{error}</p>)}
              <p className='mt-1'>Choose the failed files again to retry.</p>
            </div>
          )}
          {copyNotice && <p role='status' className='mt-3 text-sm text-slate-700'>{copyNotice}</p>}
          {loading && <p role='status' className='py-4 text-sm text-slate-500'>Loading images…</p>}
          {!loading && !visibleAssets.length && !loadError && (
            <p className='py-6 text-center text-sm text-slate-500'>{assets.length ? 'No images match your search.' : 'No images yet. Upload images to get started.'}</p>
          )}
          <div className='mt-3 grid max-h-96 grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3'>
            {visibleAssets.map((asset) => (
              <div key={asset.id} className='min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-3'>
                <div className='flex h-28 items-center justify-center rounded bg-slate-100'>
                  <img src={asset.url} alt={asset.name} loading='lazy' className='max-h-full max-w-full object-contain' />
                </div>
                <p className='mt-2 truncate text-sm font-medium text-slate-800' title={asset.name}>{asset.name}</p>
                <label className='mt-2 block text-xs text-slate-500'>
                  Image URL
                  <input ref={(input) => { if (input) urlInputs.current.set(asset.id, input); else urlInputs.current.delete(asset.id); }} aria-label={`Image URL for ${asset.name}`} value={asset.url} readOnly disabled={disabled} onFocus={(event) => event.target.select()} className='mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 disabled:opacity-50' />
                </label>
                <button type='button' onClick={() => copy(asset)} disabled={disabled} aria-label={`Copy URL for ${asset.name}`} className='mt-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50'>Copy URL</button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default BeefreeAssetsPanel;
